/**
 * Handing it over.
 *
 * ACTIVATION SEMANTICS, STATED PLAINLY, BECAUSE THEY ARE NOT WHAT THEY LOOK
 * LIKE. An organization is created `active` and there is no second activation
 * step: `sysAdminUpdateOrg` REFUSES any status but active, and says so —
 * "Suspending an organization is not implemented. Setting the status would
 * change nothing: members would keep their access."
 *
 * So there is no button here that flips a switch, because there is no switch.
 * Inventing one would be a second activation system that agrees with nothing.
 * What actually makes a customer live is their System Admin signing in and
 * their staff being invited, which is what the checklist above measures.
 */
import { formatDayUS } from '../../../../shared/dates';
import page from '../../../../shared/Page.module.css';
import type { Onboarding, StageId } from '../types';

const day = formatDayUS;

export function GoLiveStage({
  data, onGo,
}: {
  data: Onboarding; onGo: (stage: StageId) => void;
}) {
  const target = day(data.targetGoLiveDate);

  return (
    <>
      <dl className={page.facts}>
        <div><dt className={page.factTerm}>Target go-live</dt>
          <dd className={page.factValue}>{target ?? <span className={page.absent}>None given</span>}</dd></div>
        <div><dt className={page.factTerm}>Blocking items</dt>
          <dd className={page.factValue}>{data.stages.reduce((n, s) => n + (s.requirements.length ? s.blockingOpen : 0), 0)}</dd></div>
        <div><dt className={page.factTerm}>Their System Admin</dt>
          <dd className={page.factValue}>
            {data.sysAdmins.find((s) => s.enrolled)
              ? 'Signed in, second factor set up'
              : data.sysAdmins.length > 0 ? 'Signed in, no second factor yet'
              : data.pendingSysAdminInvites.length > 0 ? 'Invited, not signed in'
              : <span className={page.absent}>Nobody named</span>}
          </dd></div>
        <div><dt className={page.factTerm}>Policies loaded</dt>
          <dd className={page.factValue}>{data.policiesTotal}</dd></div>
      </dl>

      {!data.ready ? (
        <div className={`${page.notice} ${page.noticeWarn}`}>
          <strong>Not ready yet.</strong> Readiness lists what is left.
          <div className={page.actions} style={{ marginTop: 'var(--sp-3)' }}>
            <button type="button" className={page.btn} onClick={() => onGo('readiness')}>
              See what is blocking
            </button>
          </div>
        </div>
      ) : (
        <div className={`${page.notice} ${page.noticeInfo}`}>
          <strong>Ready.</strong> Everything required is done, and this organization is already
          live in the sense that matters: their System Admin can sign in, add their staff and
          publish. There is no activation switch to press — an organization is created active and
          nothing here turns it on or off.
          <p className={page.itemMeta} style={{ marginTop: 'var(--sp-2)' }}>
            What is left is a handover: tell the champion their System Admin can begin, and watch
            the first week. The operations runbook has the weekly checks.
          </p>
        </div>
      )}
    </>
  );
}
